import React from 'react';
import { WordpressSidebar } from '@benzinga/content-manager';
import { ArticleBlock as ArticleBlockType } from '@benzinga/article-manager';
import { NoFirstRender } from '@benzinga/hooks';
import { isDisplayAdBlockName, type FreestarPlacement } from '@benzinga/ads-utils';
import { RecommendedArticles } from './RecommendedArticles';

const Blocks = React.lazy(() => import('@benzinga/blocks').then(module => ({ default: module.Blocks })));
const LayoutAdmin = React.lazy(() => import('@benzinga/blocks').then(module => ({ default: module.LayoutAdmin })));

const FreestarAd = React.lazy(() =>
  import('@benzinga/ads/freestar').then(module => {
    return { default: module.FreestarAd };
  }),
);

export interface ArticleSidebarProps {
  hasAdLight?: boolean;
  followUpQuestions?: string[] | null;
  raptiveEnabled?: boolean;
  nodeId?: number;
  sidebar?: WordpressSidebar;
}

export const ArticleSidebar: React.FC<ArticleSidebarProps> = ({ hasAdLight, nodeId, raptiveEnabled, sidebar }) => {
  const hasExistingPlacement = (placement: FreestarPlacement) =>
    !!sidebar?.blocks?.find(block => isDisplayAdBlockName(block.blockName) && block.attrs?.data?.type === placement);

  const hasExistingStaticSidebarPlacement = hasExistingPlacement('sidebar_1');
  const hasExistingStickySidebarPlacement = hasExistingPlacement('sidebar_2');

  return (
    <div className="flex flex-col gap-y-4">
      <NoFirstRender>
        <React.Suspense>{sidebar && <LayoutAdmin post={sidebar} />}</React.Suspense>
      </NoFirstRender>
      {!hasExistingStaticSidebarPlacement && raptiveEnabled && (
        <React.Suspense fallback={<div className="min-h-[250px] w-full" />}>
          <FreestarAd className="w-[300px] overflow-hidden" placement="sidebar_1" />
        </React.Suspense>
      )}
      {nodeId && <RecommendedArticles articleId={nodeId as number} />}
      <React.Suspense fallback={<div className="min-h-[200px]" />}>
        {sidebar?.blocks && (
          <Blocks
            blocks={sidebar.blocks as unknown as ArticleBlockType[]}
            className="flex flex-col gap-4"
            type="sidebar"
          />
        )}
      </React.Suspense>
      {!hasExistingStickySidebarPlacement && raptiveEnabled && !hasAdLight && (
        <React.Suspense fallback={<div className="min-h-[250px] w-full" />}>
          <FreestarAd className="w-[300px]" placement="sidebar_2" />
        </React.Suspense>
      )}
    </div>
  );
};
